/**
 * /data-center — migrated from public/js/datacenter.js.
 *
 * 1. Lightbox custom class pentru `.gallery` cu nav arrows + swipe + keyboard.
 * 2. .layer-1 nav.disk navMenu pe rows.
 * 3. .layer-4 ul accordion (slideToggle).
 * 4. .layer-6 .buttons switcher pentru .col-2 .inner.info-N (cu icon swap).
 */
import { Resort } from '../core/resort.js';

interface LightboxInstance {
    target: Element;
    img: HTMLImageElement;
    ui: HTMLElement;
    modal: HTMLElement;
    loader: HTMLElement;
    arrowLeft: HTMLElement;
    arrowRight: HTMLElement;
    labelTitle: HTMLElement;
    labelCounter: HTMLElement;
    close: HTMLElement;
    title: string | null;
    square: NodeListOf<Element>;
    index: number;
    opened: boolean;
    busy: boolean;
    widget: { label: { position: string } };
    ekey: number;
    resize: (load?: boolean) => void;
    update: () => void;
    updateLabel: () => void;
    create: (a: Element, update?: boolean) => void;
    hidden: () => void;
    destroy: () => void;
    setEvents: () => void;
    prevIm: () => void;
    nextIm: () => void;
}

function makeLightbox(selector: string): LightboxInstance | null {
    const target = $(selector) as Element | null;
    if (!target) return null;
    if (target.data('lightbox')) return null;

    const lb = {} as LightboxInstance;
    lb.target = target;
    target.data('lightbox', lb);

    lb.opened = false;
    lb.busy = false;
    lb.widget = ((window as unknown as { intData: { widget: { lightbox: { label: { position: string } } } } }).intData?.widget?.lightbox) ?? { label: { position: '%index/%count' } };

    lb.resize = function (load?: boolean): void {
        lb.img.style.removeProperty('width');
        lb.img.style.removeProperty('height');
        let imWidth  = parseFloat(lb.img.getProp('width').split('px')[0]  ?? '0');
        let imHeight = parseFloat(lb.img.getProp('height').split('px')[0] ?? '0');
        const viewWidth  = window.innerWidth  - 0.1 * window.innerWidth;
        const viewHeight = window.innerHeight - 0.2 * window.innerHeight;

        if (imWidth > viewWidth) {
            lb.img.style.width  = `${viewWidth}px`;
            lb.img.style.height = 'auto';
        } else if (imHeight > viewHeight) {
            lb.img.style.height = `${viewHeight}px`;
            lb.img.style.width  = 'auto';
        }
        imWidth  = parseFloat(lb.img.getProp('width').split('px')[0]  ?? '0');
        imHeight = parseFloat(lb.img.getProp('height').split('px')[0] ?? '0');
        lb.ui.style.width  = `${imWidth}px`;
        lb.ui.style.height = `${imHeight}px`;
        if (load) lb.update();
    };

    lb.update = function (): void {
        lb.img.classList.remove('hidden');
        lb.ui.append(lb.img);
        lb.img.focus();
        lb.img.classList.add('loaded');
        lb.loader.classList.add('hidden');
        lb.updateLabel();
        lb.busy = false;
    };

    lb.updateLabel = function (): void {
        if (lb.title) {
            lb.labelTitle.classList.remove('hide');
            lb.labelTitle.textContent = lb.title;
        } else {
            lb.labelTitle.classList.add('hide');
        }
        lb.labelCounter.textContent = lb.widget.label.position
            .replace('%index', String(lb.index + 1))
            .replace('%count', String(lb.square.length));
    };

    lb.create = function (a: Element, update?: boolean): void {
        lb.busy = true;
        lb.title = a.getAttribute('title');
        const src = a.getAttribute('href') ?? '';

        if (update) {
            lb.loader.classList.remove('hidden');
            lb.img.classList.add('hidden');
            lb.img.setAttribute('src', src);
        } else {
            lb.modal = $('<div class="sp-lightbox-overlay" />') as HTMLElement;
            lb.ui = $('<div class="sp-lightbox-container" />') as HTMLElement;
            lb.loader = $('<div class="loader" />') as HTMLElement;
            lb.arrowLeft  = $('<a class="nav-arrow arrow-left"></a>')  as HTMLElement;
            lb.arrowRight = $('<a class="nav-arrow arrow-right"></a>') as HTMLElement;

            const label = $('<div class="nav-label"><div><div class="label-title"></div><div class="label-number"></div></div><a href="#" class="close-popup"><span class="container-close"><span class="border border-1"></span><span class="border border-2"></span></span><span class="close-btn">ÎNCHIDE</span></a></div>') as HTMLElement;
            lb.labelTitle   = label.find('.label-title')   as HTMLElement;
            lb.labelCounter = label.find('.label-number')  as HTMLElement;
            lb.close        = label.find('.close-popup')   as HTMLElement;

            lb.modal.append(lb.ui);
            lb.ui.append(lb.loader);
            if (!window.device.touchCapable) {
                lb.ui.append(lb.arrowLeft);
                lb.ui.append(lb.arrowRight);
            }
            lb.ui.append(label);
            document.body.append(lb.modal);

            lb.img = $(`<img src="${src}" />`) as HTMLImageElement;
            lb.opened = true;
        }

        lb.img.classList.add('hidden');
        lb.img.classList.remove('loaded');
        document.body.append(lb.img);

        lb.arrowRight.classList.toggle('hide', lb.square.length === lb.index + 1);
        lb.arrowLeft.classList.toggle('hide', !lb.index);

        const widgetMap = (window as unknown as { Resort: { widget: { lightbox: Map<unknown, unknown> } } }).Resort.widget.lightbox;
        lb.ekey = widgetMap.size;
        widgetMap.set(lb.ekey, lb);
    };

    lb.hidden = function (): void {
        lb.modal.remove();
        lb.busy = false;
        lb.opened = false;
        const winOff = (window as unknown as { off: (e: string) => void }).off;
        winOff(`keyup.lightbox${lb.ekey} resize.lightbox${lb.ekey} orientationchange.lightbox${lb.ekey}`);
    };

    lb.destroy = function (): void {
        lb.modal.remove();
        lb.target.off('click.lightbox');
        const winOff = (window as unknown as { off: (e: string) => void }).off;
        winOff(`keyup.lightbox${lb.ekey} resize.lightbox${lb.ekey} orientationchange.lightbox${lb.ekey}`);
        lb.target.removeData('lightbox');
    };

    lb.setEvents = function (): void {
        let tm: number | null = null;

        lb.target.on('click.lightbox', 'a', function (this: Element, e: Event) {
            if (this.getAttribute('target')) return;
            e.preventDefault();
            if (lb.opened) return;

            lb.square = lb.target.find('a:not([target])', true) as NodeListOf<Element>;
            lb.index  = this.index(lb.square);
            lb.create(this);

            lb.ui.addEventListener('transitionend', function (e2: Event) {
                const ev = e2 as TransitionEvent;
                if (lb.busy && (ev.propertyName === 'width' || ev.propertyName === 'height')) lb.update();
            });

            lb.img.on('load', () => { lb.resize(true); });
            lb.ui.on('click', (e2: Event) => { e2.stopPropagation(); });
            lb.arrowLeft.on('click',  () => { lb.prevIm(); });
            lb.arrowRight.on('click', () => { lb.nextIm(); });
            lb.ui.on('swipeleft',  () => { lb.nextIm(); });
            lb.ui.on('swiperight', () => { lb.prevIm(); });
            lb.modal.on('click', () => { lb.hidden(); });
            lb.close.on('click', (e2: Event) => { e2.stopPropagation(); lb.hidden(); });

            (window as unknown as { on: (e: string, h: () => void) => void }).on(
                `resize.popup.${lb.ekey} orientationchange.popup${lb.ekey}`,
                () => {
                    if (tm !== null) { clearTimeout(tm); tm = null; }
                    tm = window.setTimeout(() => lb.resize(), 300);
                },
            );

            (window as unknown as { on: (e: string, h: (e: KeyboardEvent) => void) => void }).on(
                `keydown.popup${lb.ekey}`,
                (e2: KeyboardEvent) => {
                    switch (e2.keyCode) {
                        case 37: lb.prevIm(); break;
                        case 39: lb.nextIm(); break;
                        case 27: lb.hidden(); break;
                    }
                },
            );
        });
    };

    lb.prevIm = function (): void {
        if (lb.busy || !lb.index) return;
        lb.index--;
        lb.create(lb.square.item(lb.index)!, true);
    };

    lb.nextIm = function (): void {
        if (lb.busy || lb.square.length === lb.index + 1) return;
        lb.index++;
        lb.create(lb.square.item(lb.index)!, true);
    };

    lb.setEvents();
    return lb;
}

new Resort(() => {
    makeLightbox('.gallery');

    ($('.layer-1 nav.disk') as Element | null)?.navMenu('a', '.layer-1 .row');

    ($('.layer-4 ul > li', true) as NodeListOf<Element>).on('click', 'a', function (this: Element) {
        this.classList.toggle('opened');
        (this.next('ul') as HTMLElement | null)?.slideToggle();
    });

    ($('.layer-6 .buttons') as Element | null)?.on('click', 'a', function (this: Element) {
        if (this.classList.contains('active')) return;
        ($('.layer-6 .buttons a.active') as Element | null)?.classList.remove('active');
        this.classList.add('active');

        const icon = $('.layer-6 .circle .icon') as HTMLElement | null;
        if (icon) {
            icon.className = '';
            const id = (this as HTMLElement).dataset['id'];
            icon.classList.add('icon', `icon-${id}`);
        }

        const id = (this as HTMLElement).dataset['id'];
        ($('.layer-6 .col-2 .inner.active') as Element | null)?.classList.remove('active');
        ($(`.layer-6 .col-2 .inner.info-${id}`) as Element | null)?.classList.add('active');
    });
});
